import BaseCollectionService from "./base.collection.service";
import { ResponseWrapper } from "@/services/response_wrapper";
import { ErrorWrapper } from "@/services/error_wrapper";

export interface ITransaction {
  _id: string;
  createdBy?: string;
  transactionPolicyNumber: number;
  policyPrefix: string;
  status: string;
  onFiserv?: boolean;
  customerName?: string;
  transactionId?: number;
  transactionDate?: Date;
  timeZone?: string;
  sessionId?: string;
  paymentCategory?: string;
  lastFourDigits?: number;
  paymentAmount?: number;
  holderZip?: number;
  totalAmount?: number;
  convenienceFee?: number;
  clientReferenceData?: string;
  cardType?: string;
  cardExpirationYear?: number;
  cardExpirationMonth?: number;
  batchNumber?: number;
  authCode?: string;
  tokenId?: string;
  transactionDescription?: string;
  mode?: string;
  responseMessage?: string;
}

class TransactionsService extends BaseCollectionService {
  constructor() {
    super("transactions");
  }

  /**
   * Create a transaction with duplicate key error handling
   * If a transaction with the same transactionId already exists, treat as success
   * @param payload Transaction data
   * @returns ResponseWrapper with transaction data
   */
  public async createOne(payload: any): Promise<ResponseWrapper> {
    try {
      return await this.request({
        method: "post",
        url: "/transactions",
        data: payload
      });
    } catch (error) {
      // Check if this is a MongoDB duplicate key error for transactionId
      if (this.isDuplicateTransactionIdError(error)) {
        // If the transaction already exists, fetch and return it
        return await this.handleDuplicateTransaction(
          payload,
          error as ErrorWrapper
        );
      }

      // Re-throw any other errors
      throw error;
    }
  }

  /**
   * Check if the error is a MongoDB duplicate key error for transactionId
   */
  private isDuplicateTransactionIdError(error: any): boolean {
    if (!(error instanceof ErrorWrapper)) {
      return false;
    }

    const errorMessage = error.message || "";
    const rawMessage = error.raw?.response?.data?.response?.message || "";

    // Check for MongoDB duplicate key error pattern for transactionId
    const duplicateKeyPattern = /E11000 duplicate key error.*transactionId.*dup key/i;

    return (
      duplicateKeyPattern.test(errorMessage) ||
      duplicateKeyPattern.test(rawMessage)
    );
  }

  /**
   * Handle duplicate transaction by fetching existing transaction
   * Since we can't query by transactionId directly, we'll return a success response
   * indicating the transaction was already processed
   */
  private async handleDuplicateTransaction(
    payload: Partial<ITransaction>,
    error: ErrorWrapper
  ): Promise<ResponseWrapper> {
    const rawResponse = error.raw?.response;
    // Create a mock successful response indicating the transaction already exists
    const mockResponse = {
      data: {
        response: {
          success: true,
          message: "Transaction already exists",
          data: {
            ...payload,
            _id: `existing_${payload.transactionId}`,
            status: "succeeded"
          }
        }
      },
      status: rawResponse?.status ?? 200,
      statusText: rawResponse?.statusText ?? "OK",
      headers: rawResponse?.headers ?? {},
      config: rawResponse?.config ?? {}
    };

    return new ResponseWrapper(mockResponse);
  }
}

export default new TransactionsService();
